import React, { useState, DragEvent } from 'react';
import { Moment } from 'moment';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import clsx from 'clsx';
import { useTranslation } from 'common/i18n/hooks';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { ButtonReact } from 'common/components/button';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { DateRangePickerReact as DatePickerReact } from 'common/widgets/date-range-picker';
import { DatePickerMode, getDateRangeLabel } from 'common/widgets/date-range-picker-new/util';
import { DateRangeType, DisplayContext, TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { ItemsMenuReact } from 'common/components/items-menu';
import { DateRange, DateRangeAttributes } from 'common/widgets/date-range-picker/models/date-range';
import { ButtonTheme } from 'common/components/button/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { DisplaySize } from 'common/components/tooltip/types';
import { CurrentProject } from 'common/report/model-resources/projects';
import { FeatureGate } from 'common/report/constants';
import { getDateRangeComparisonOptions } from 'common/report/util';
import { DateRangeFactory } from 'common/widgets/date-range-picker/models/date-range-factory';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { InputReact } from 'common/components/input';
import { InputSize, InputType } from 'common/components/input/types';
import { FullLengthDateRange } from 'common/widgets/date-range-picker/models/full-length-date-range';
import { ItemClickEvent, Section } from 'common/components/items-menu/types';
import { getCurrentMoment } from 'common/util/date';
import { ComparisonDate } from 'common/query-builder/query-block/time-comparison-menu/types';
import { MenuChangeEvent } from 'common/components/select/types';
import { getTimeComparisonRangeText } from 'common/query-builder/query-block/time-comparison-menu/utils';
import { ButtonBarReact } from 'common/components/button-bar';
import { KEY_VALUES } from 'common/util/dom';
import { TimeRangeAndComparison } from 'common/report/canvas/types';
import styles from './index.module.styl';
enum DropPosition {
  Above,
  Below,
  On,
}
function TimeRangeMenu(_ref: {
  timeRange: DateRangeAttributes;
  asOfDate: string;
  canUseRolling: boolean;
  maxHistoryDays: number;
  onTimeRangeChange: (newDateRange: DateRangeAttributes) => void;
}) {
  var _presetOptions$find;
  let {
    timeRange,
    asOfDate,
    canUseRolling,
    maxHistoryDays,
    onTimeRangeChange
  }: {
    timeRange: DateRangeAttributes;
    asOfDate: string;
    canUseRolling: boolean;
    maxHistoryDays: number;
    onTimeRangeChange: (newDateRange: DateRangeAttributes) => void;
  } = _ref;
  const {
    print
  } = useTranslation();
  const [timeRangeMenuState, setTimeRangeMenuState] = useState<null | `list` | `custom`>(null);
  const displayContext = timeRange.rolling ? DisplayContext.FullLength : canUseRolling ? DisplayContext.RelativeRange : DisplayContext.Default;
  const presetOptions = FullLengthDateRange.getPresetOptions();
  const relativePresets = presetOptions.filter(option => option.value.type === DateRangeType.RelativeAfter);
  const sincePresets = presetOptions.filter(option => option.value.type === DateRangeType.Since);
  const timeRangePresetOptions: Section[] = [{
    items: [{
      value: "custom",
      label: "Custom"
    }, ...relativePresets.map(option => ({
      value: option.value,
      label: option.label
    })), {
      value: "xtd",
      label: "XTD",
      subSections: [{
        items: sincePresets.map(option => ({
          value: option.value,
          label: option.label
        }))
      }]
    }]
  }];
  const selectedLabel = print(getDateRangeLabel({
    dateRange: new DateRange(timeRange),
    matchingPreset: (_presetOptions$find = presetOptions.find(option => isEqual(option.value, timeRange))) !== null && _presetOptions$find !== void 0 ? _presetOptions$find : null,
    projectAsOfDate: null,
    now: getCurrentMoment({
      localized: true
    }) as Moment
  }));
  const handleMenuChange = (ev: MenuChangeEvent) => {
    if (ev.detail.isOpen) {
      setTimeRangeMenuState("list");
    }
  };
  const handlePresetClick = (ev?: ItemClickEvent) => {
    var _ev$detail;
    const value: string | DateRangeAttributes = ev === null || ev === void 0 || (_ev$detail = ev.detail) === null || _ev$detail === void 0 || (_ev$detail = _ev$detail.item) === null || _ev$detail === void 0 ? void 0 : _ev$detail.value;
    if (value === "custom") {
      setTimeRangeMenuState("custom");
    } else if (value && typeof value !== "string") {
      onTimeRangeChange(value);
      setTimeRangeMenuState(null);
    }
  };
  const handleDateRangeApply = (dateRange: DateRange) => {
    onTimeRangeChange(dateRange.getAttributes());
    setTimeRangeMenuState(null);
  };
  return <SelectReact theme={SelectTheme.Secondary} className={styles.timeRange} alwaysShowCaret label={selectedLabel} onMenuChange={handleMenuChange} open={timeRangeMenuState !== null} data-sentry-element="SelectReact" data-sentry-component="TimeRangeMenu" data-sentry-source-file="index.tsx">
      <div slot='body'>
        {timeRangeMenuState === "custom" && <DateRangePickerReact {...asOfDate !== "today" ? {
        projectAsOfDate: asOfDate
      } : {}} datePickerModesOptions={[DatePickerMode.InTheLast]} disableSubDayGranularityMessage='Sub-day granularity is not supported in canvas' disableCalendarDayTooltip displayContext={displayContext} dateRange={timeRange} maxHistoryDays={maxHistoryDays} isTimePickerEnabled={false} forceHidePresetMenu onApply={ev => handleDateRangeApply(ev.detail.dateRange)} onCancel={() => setTimeRangeMenuState(null)} />}
        {timeRangeMenuState === "list" && <ItemsMenuReact style={{
        width: "150px"
      }} sections={timeRangePresetOptions} onClickItem={handlePresetClick} />}
      </div>
    </SelectReact>;
}
function TimeComparisonMenu(_ref2: {
  timeComparison: ComparisonDate;
  timeRange: DateRangeAttributes;
  maxHistoryDays: number;
  projectAsOfDate: string;
  onTimeComparisonChange: (newTimeComparison: ComparisonDate) => void;
}) {
  let {
    timeComparison,
    timeRange,
    maxHistoryDays,
    projectAsOfDate,
    onTimeComparisonChange
  }: {
    timeComparison: ComparisonDate;
    timeRange: DateRangeAttributes;
    maxHistoryDays: number;
    projectAsOfDate: string;
    onTimeComparisonChange: (newTimeComparison: ComparisonDate) => void;
  } = _ref2;
  const {
    print
  } = useTranslation();
  const [timeComparisonMenuState, setTimeComparisonMenuState] = useState<null | `list` | `custom`>(null);
  const [stagedComparisonDate, setStagedComparisonDate] = useState<ComparisonDate | null>(null);
  const [stagedComparisonType, setStagedComparisonType] = useState<DateRangeType.StartsOn | DateRangeType.EndsOn>(DateRangeType.StartsOn);
  const timeComparisonPresetOptions = getDateRangeComparisonOptions(maxHistoryDays, {});
  const comparisonType = stagedComparisonType ? stagedComparisonType : timeComparison.from ? DateRangeType.StartsOn : DateRangeType.EndsOn;
  const comparisonDateRange = DateRangeFactory.createDateRange({
    type: comparisonType
  }, {
    displayContext: DisplayContext.RelativeRange
  });
  if (stagedComparisonDate) {
    var _stagedComparisonDate;
    comparisonDateRange.from = (_stagedComparisonDate = stagedComparisonDate.from) !== null && _stagedComparisonDate !== void 0 ? _stagedComparisonDate : stagedComparisonDate.to;
  } else if (timeComparison) {
    const {
      to,
      from
    } = timeComparison;
    comparisonDateRange.from = from !== null && from !== void 0 ? from : to;
  }
  if (comparisonDateRange.from && DateRange.isRelativeDate(comparisonDateRange.from)) {
    comparisonDateRange.rolling = true;
  }
  const dateRange = new DateRange(timeRange).getCalendarDatesAsOf(projectAsOfDate);
  let maxDate = dateRange.to;
  if (comparisonType === DateRangeType.StartsOn) {
    maxDate = dateRange.from;
  }
  const selectedLabel = print(getTimeComparisonRangeText(timeComparison));
  const handleMenuChange = (ev: MenuChangeEvent) => {
    if (ev.detail.isOpen) {
      setTimeComparisonMenuState("list");
    }
  };
  const handlePresetClick = (ev?: ItemClickEvent) => {
    var _ev$detail2;
    const value = ev === null || ev === void 0 || (_ev$detail2 = ev.detail) === null || _ev$detail2 === void 0 || (_ev$detail2 = _ev$detail2.item) === null || _ev$detail2 === void 0 ? void 0 : _ev$detail2.value;
    if (value === "custom") {
      setTimeComparisonMenuState("custom");
    } else if (value && typeof value !== "string") {
      onTimeComparisonChange({
        offset: value
      });
      setTimeComparisonMenuState(null);
    }
  };
  const handleDateRangeChange = (newDateRange: DateRange) => {
    setStagedComparisonDate(newDateRange.type === DateRangeType.StartsOn ? {
      from: newDateRange.from
    } : {
      to: newDateRange.to
    });
    setStagedComparisonType(newDateRange.type === DateRangeType.StartsOn ? DateRangeType.StartsOn : DateRangeType.EndsOn);
  };
  const handleDateRangeApply = () => {
    if (!stagedComparisonDate) {
      return;
    }
    onTimeComparisonChange(stagedComparisonDate);
    setTimeComparisonMenuState(null);
  };
  return <SelectReact theme={SelectTheme.Secondary} className={styles.compareTo} alwaysShowCaret label={selectedLabel} onMenuChange={handleMenuChange} open={timeComparisonMenuState !== null} data-sentry-element="SelectReact" data-sentry-component="TimeComparisonMenu" data-sentry-source-file="index.tsx">
      <div slot='body'>
        {timeComparisonMenuState === "custom" && <>
            <div className={styles.customComparison}>
              <DatePickerReact displayContext={DisplayContext.RelativeRange} dateRange={comparisonDateRange} maxDate={maxDate} maxHistoryDays={String(maxHistoryDays)} onChangeDateRange={ev => handleDateRangeChange(ev.detail.dateRange)} />
            </div>
            <ButtonBarReact onClick={handleDateRangeApply}>Apply</ButtonBarReact>
          </>}
        {timeComparisonMenuState === "list" && <ItemsMenuReact sections={timeComparisonPresetOptions} onClickItem={handlePresetClick} />}
      </div>
    </SelectReact>;
}
function TimeRangeAndComparisonItem(_ref3: {
  index: number;
  timeRangeAndComparison: TimeRangeAndComparison;
  asOfDate: string;
  canUseRolling: boolean;
  onTimeRangeChange: (newTimeRange: DateRangeAttributes, index: number) => void;
  onTimeComparisonChange: (newTimeComparison: ComparisonDate, index: number) => void;
  onDateRangeTitleInputChange: (newTitle: string, index: number) => void;
  onTimeRangeAndComparisonRemove: (index: number) => void;
  onItemDrop: ({
    from,
    to,
    dropPosition
  }: {
    from: number;
    to: number;
    dropPosition: null | DropPosition;
  }) => void;
}) {
  var _useCurrentProject$ge, _useCurrentProject$ge2, _currentProject$proje;
  let {
    index,
    timeRangeAndComparison,
    asOfDate,
    canUseRolling,
    onTimeRangeChange,
    onTimeComparisonChange,
    onDateRangeTitleInputChange,
    onTimeRangeAndComparisonRemove,
    onItemDrop
  }: {
    index: number;
    timeRangeAndComparison: TimeRangeAndComparison;
    asOfDate: string;
    canUseRolling: boolean;
    onTimeRangeChange: (newTimeRange: DateRangeAttributes, index: number) => void;
    onTimeComparisonChange: (newTimeComparison: ComparisonDate, index: number) => void;
    onDateRangeTitleInputChange: (newTitle: string, index: number) => void;
    onTimeRangeAndComparisonRemove: (index: number) => void;
    onItemDrop: ({
      from,
      to,
      dropPosition
    }: {
      from: number;
      to: number;
      dropPosition: null | DropPosition;
    }) => void;
  } = _ref3;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dropPosition, setDropPosition] = useState<null | DropPosition>(null);
  const {
    timeRange,
    timeComparison,
    title
  } = timeRangeAndComparison;
  const currentProject: CurrentProject | null = (_useCurrentProject$ge = (_useCurrentProject$ge2 = useCurrentProject().get()) === null || _useCurrentProject$ge2 === void 0 ? void 0 : _useCurrentProject$ge2.value) !== null && _useCurrentProject$ge !== void 0 ? _useCurrentProject$ge : null;
  const maxHistoryDays = (currentProject === null || currentProject === void 0 ? void 0 : currentProject.getFeatureGate(FeatureGate.MaxDataHistoryDays)) || 365 * 5;
  const getDropPositoin = (ev: DragEvent) => {
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    if (ev.clientY - (rect.top + rect.height / 3) < 0) {
      return DropPosition.Above;
    } else if (ev.clientY - (rect.bottom - rect.height / 3) > 0) {
      return DropPosition.Below;
    }
    return DropPosition.On;
  };
  const handleDragStart = (ev: DragEvent) => {
    ev.dataTransfer.effectAllowed = "move";
    ev.dataTransfer.setData("text/plain", index.toString());
    setIsDragging(true);
  };
  const handleDragOver = (ev: DragEvent) => {
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "move";
    setDropPosition(getDropPositoin(ev));
  };
  const handleDrop = (ev: DragEvent) => {
    ev.stopPropagation();
    const fromIndex = parseInt(ev.dataTransfer.getData("text/plain"), 10);
    onItemDrop({
      from: fromIndex,
      to: index,
      dropPosition
    });
    setIsDragging(false);
    setDropPosition(null);
  };
  const handleDragLeave = () => {
    setDropPosition(null);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
    setDropPosition(null);
  };
  return <div key={"time-range-and-comparison-".concat(index)} className={clsx(styles.option, {
    [styles.isDragging]: isDragging,
    [styles.dropIndicatorAbove]: dropPosition === DropPosition.Above,
    [styles.dropIndicatorBelow]: dropPosition === DropPosition.Below,
    [styles.dropIndicatorOn]: dropPosition === DropPosition.On
  })} draggable onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} onDragLeave={handleDragLeave} onDragEnd={handleDragEnd} data-sentry-component="TimeRangeAndComparisonItem" data-sentry-source-file="index.tsx">
      <div className={styles.dragGripper}>
        <SvgIconReact icon={"gripper-xs"} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      </div>
      <TimeRangeMenu timeRange={timeRange} asOfDate={asOfDate} maxHistoryDays={maxHistoryDays} canUseRolling={canUseRolling} onTimeRangeChange={newTimeRange => onTimeRangeChange(newTimeRange, index)} data-sentry-element="TimeRangeMenu" data-sentry-source-file="index.tsx" />
      <TimeComparisonMenu timeComparison={timeComparison} timeRange={timeRange} maxHistoryDays={maxHistoryDays} projectAsOfDate={(_currentProject$proje = currentProject === null || currentProject === void 0 ? void 0 : currentProject.projectAsOfDate) !== null && _currentProject$proje !== void 0 ? _currentProject$proje : ""} onTimeComparisonChange={newTimeComparison => onTimeComparisonChange(newTimeComparison, index)} data-sentry-element="TimeComparisonMenu" data-sentry-source-file="index.tsx" />
      <InputReact className={styles.timeRangeTitle} type={InputType.Text} value={title} size={InputSize.Medium} maxlength={10} onBlur={ev => onDateRangeTitleInputChange(ev.target.value as string, index)} onKeydown={ev => {
      if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        (ev.target as HTMLInputElement).blur();
      }
    }} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
      {index !== 0 && <ButtonReact theme={ButtonTheme.Tertiary} icon={"trashcan"} onClick={() => onTimeRangeAndComparisonRemove(index)} />}
    </div>;
}
export function TimeRangesAndComparisonsMenu(props: {
  timeRangesAndComparisons: TimeRangeAndComparison[];
  asOfDate: string;
  hasTimeRangesAndComparisonsChanged?: boolean;
  forSingleMetric?: boolean;
  onTimeRangeAndComparisonChange: (newTimeRangeAndComparisons: TimeRangeAndComparison[]) => void;
  onReset?: () => void;
}) {
  const handleTimeRangeChange = (newTimeRange: DateRangeAttributes, index: number) => {
    const newTimeRangesAndComparisons = cloneDeep(props.timeRangesAndComparisons);
    newTimeRangesAndComparisons[index].timeRange = newTimeRange;
    props.onTimeRangeAndComparisonChange(newTimeRangesAndComparisons);
  };
  const handleTimeComparisonChange = (newTimeComparison: ComparisonDate, index: number) => {
    const newTimeRangesAndComparisons = cloneDeep(props.timeRangesAndComparisons);
    newTimeRangesAndComparisons[index].timeComparison = newTimeComparison;
    props.onTimeRangeAndComparisonChange(newTimeRangesAndComparisons);
  };
  const handleDateRangeTitleInputChange = (newTitle: string, index: number) => {
    const newTimeRangesAndComparisons = cloneDeep(props.timeRangesAndComparisons);
    newTimeRangesAndComparisons[index].title = newTitle;
    props.onTimeRangeAndComparisonChange(newTimeRangesAndComparisons);
  };
  const handleTimeRangeAndComparisonAdd = () => {
    // add new time range and compare period
    props.onTimeRangeAndComparisonChange([...props.timeRangesAndComparisons, {
      timeRange: {
        type: DateRangeType.RelativeAfter,
        window: {
          unit: TimeUnit.Day,
          value: 7
        }
      },
      timeComparison: {
        offset: {
          unit: TimeUnit.Day,
          value: 1
        }
      },
      title: ""
    }]);
  };
  const handleTimeRangeAndComparisonRemove = (index: number) => {
    // remove time range and compare period
    const newTimeRangesAndComparisons = cloneDeep(props.timeRangesAndComparisons);
    newTimeRangesAndComparisons.splice(index, 1);
    props.onTimeRangeAndComparisonChange(newTimeRangesAndComparisons);
  };
  const handleItemDrop = (_ref4: {
    from: number;
    to: number;
    dropPosition: null | DropPosition;
  }) => {
    let {
      from,
      to,
      dropPosition
    }: {
      from: number;
      to: number;
      dropPosition: null | DropPosition;
    } = _ref4;
    if (!dropPosition) return;
    const newTimeRangesAndComparisons = cloneDeep(props.timeRangesAndComparisons);
    newTimeRangesAndComparisons.splice(to, 0, newTimeRangesAndComparisons.splice(from, 1)[0]);
    props.onTimeRangeAndComparisonChange(newTimeRangesAndComparisons);
  };
  return <div className={styles.timeRangesAndComparisonsMenu} data-sentry-component="TimeRangesAndComparisonsMenu" data-sentry-source-file="index.tsx">
      <div className={styles.menuHeader}>
        <div className={styles.titleAndDescription}>
          <span className={styles.title}>Time Ranges and Compare Periods</span>
          <span className={styles.description}>
            {props.forSingleMetric ? "You may customize these values for this metric" : "Specify up to three defaults. Individual metrics may override this."}
          </span>
        </div>
        {props.onReset && <ButtonReact className={styles.resetButton} disabled={!props.hasTimeRangesAndComparisonsChanged} theme={ButtonTheme.Tertiary} tooltip={{
        text: "Reset to default",
        size: DisplaySize.Small
      }} onClick={props.onReset}>
            Reset
          </ButtonReact>}
      </div>
      <div className={styles.timeRangesList}>
        <div className={styles.headers}>
          <div className={styles.header}>
            <span>Time Range</span>
            <SvgIconReact icon={"info"} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
            <TooltipReact text={"The selected time range used to filter data"} size={DisplaySize.Small} data-sentry-element="TooltipReact" data-sentry-source-file="index.tsx" />
          </div>
          <div className={styles.header}>
            <span>Compare to</span>
            <SvgIconReact icon={"info"} data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
            <TooltipReact text={"The look back period to compare a time period of the same length"} size={DisplaySize.Small} data-sentry-element="TooltipReact" data-sentry-source-file="index.tsx" />
          </div>
          <span className={styles.header}>Title</span>
        </div>
        {props.timeRangesAndComparisons.map((timeRangeAndComparison, index) => <TimeRangeAndComparisonItem index={index} timeRangeAndComparison={timeRangeAndComparison} asOfDate={props.asOfDate} canUseRolling={Boolean(props.forSingleMetric)} onTimeRangeChange={handleTimeRangeChange} onTimeComparisonChange={handleTimeComparisonChange} onDateRangeTitleInputChange={handleDateRangeTitleInputChange} onTimeRangeAndComparisonRemove={handleTimeRangeAndComparisonRemove} onItemDrop={handleItemDrop} />)}
        <ButtonReact className={styles.addButton} theme={ButtonTheme.Tertiary} icon='plus' disabled={props.timeRangesAndComparisons.length >= 3} onClick={handleTimeRangeAndComparisonAdd} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
          Add
        </ButtonReact>
      </div>
    </div>;
}